<template>
  <div class="icon-selector">
    <!-- Trigger button -->
    <button
      :class="['icon-trigger-btn', styleClass]"
      @click.prevent="toggleDropdown"
      :aria-expanded="state.iconSearchOpen"
    >
      <div class="icon-trigger-preview">
        <span v-if="selectedIcon" class="icon-trigger-selected">
          <font-awesome-icon :icon="selectedIcon" style="font-size: 15px" />
        </span>
        <span v-else class="icon-trigger-placeholder">
          <font-awesome-icon
            :icon="['fas', 'tag']"
            style="font-size: 14px; opacity: 0.4"
          />
        </span>
        <span class="icon-trigger-label">{{
          selectedIcon ? 'Change Icon' : buttonText
        }}</span>
      </div>
      <font-awesome-icon
        :icon="['fas', 'caret-down']"
        class="icon-trigger-caret"
        :class="{ rotated: state.iconSearchOpen }"
      />
    </button>

    <!-- Dropdown panel -->
    <transition name="icon-panel-slide">
      <div v-if="state.iconSearchOpen" class="icon-panel">
        <div class="icon-search-wrap">
          <font-awesome-icon
            :icon="['fas', 'magnifying-glass']"
            class="icon-search-magnifier"
          />
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search icons..."
            class="icon-search-input"
            ref="searchInputRef"
          />
          <button
            v-if="searchQuery"
            class="icon-search-clear"
            @click.prevent="searchQuery = ''"
          >
            <font-awesome-icon :icon="['fas', 'circle-xmark']" />
          </button>
        </div>

        <div class="icon-grid" v-if="filteredIcons.length > 0">
          <div
            v-for="(icon, index) in filteredIcons"
            :key="index"
            class="icon-item"
            :class="{
              selected: selectedIcon && selectedIcon.iconName === icon.iconName,
            }"
            @click="selectIcon(icon)"
            :title="icon.iconName"
          >
            <font-awesome-icon :icon="icon" />
          </div>
        </div>
        <div v-else class="icon-empty">
          <p>No icons found for "{{ searchQuery }}"</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, reactive, watch, nextTick } from 'vue';
import { iconsArray } from '../utils/iconLibrary';

export default {
  name: 'IconSelector',
  props: {
    color: {
      type: String,
      default: 'grey-outline-dropdown',
    },
    buttonText: {
      type: String,
      required: true,
    },
    styleClass: {
      type: String,
      default: '',
    },
    options: {
      type: String,
      default: () => '',
    },
  },
  setup(props, context) {
    const allIcons = ref(iconsArray);
    const searchQuery = ref('');
    const loading = ref(false);
    const selectedIcon = ref(null);
    const searchInputRef = ref(null);
    const state = reactive({
      iconSearchOpen: false,
    });

    const toggleDropdown = () => {
      state.iconSearchOpen = !state.iconSearchOpen;
      if (state.iconSearchOpen) {
        nextTick(() => {
          searchInputRef.value?.focus();
        });
      }
    };

    const filteredIcons = computed(() => {
      return allIcons.value.filter((icon) =>
        icon.iconName.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    });

    const selectIcon = (icon) => {
      state.iconSearchOpen = false;
      selectedIcon.value = icon;
      searchQuery.value = '';
      context.emit('optionSelected', `${icon.prefix} ${icon.iconName}`);
    };

    const mapIconFromString = (iconString) => {
      if (!iconString) return null;
      const [prefix, iconName] = iconString.split(' ');
      return (
        allIcons.value.find(
          (icon) => icon.prefix === prefix && icon.iconName === iconName
        ) || null
      );
    };

    watch(
      () => props.options,
      (newValue) => {
        if (newValue && props.options.length > 0) {
          selectedIcon.value = mapIconFromString(newValue);
        }
      },
      { immediate: true }
    );

    return {
      allIcons,
      loading,
      searchQuery,
      filteredIcons,
      selectIcon,
      props,
      context,
      state,
      toggleDropdown,
      selectedIcon,
      searchInputRef,
    };
  },
};
</script>

<style scoped>
.icon-selector {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
}

.icon-trigger-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: 1.5px solid #e7e7e7;
  border-radius: 10px;
  padding: 10px 12px;
  background: white;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  box-sizing: border-box;
}
.dark-theme .icon-trigger-btn {
  background: #171e29;
  border-color: #2a3a4e;
}
.icon-trigger-btn:hover {
  border-color: #7f77dd;
}
.icon-trigger-btn:focus {
  outline: none;
  border-color: #7f77dd;
  box-shadow: 0 0 0 3px rgba(127, 119, 221, 0.1);
}

.icon-trigger-preview {
  display: flex;
  align-items: center;
  gap: 10px;
}
.icon-trigger-selected {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: var(--purple-50);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent-purple);
}
.dark-theme .icon-trigger-selected {
  background: #1e1a40;
}
.icon-trigger-placeholder {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: #f4f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
}
.dark-theme .icon-trigger-placeholder {
  background: #2a3a4e;
}
.icon-trigger-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #213547;
}
.dark-theme .icon-trigger-label {
  color: #d4d2d2;
}
.icon-trigger-caret {
  color: #888;
  font-size: 0.85rem;
  transition: transform 0.2s ease;
}
.icon-trigger-caret.rotated {
  transform: rotate(180deg);
  color: var(--color-accent-purple);
}

.icon-panel {
  margin-top: 6px;
  background: white;
  border: 1.5px solid #e7e7e7;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  padding: 0.75rem;
  z-index: 10;
}
.dark-theme .icon-panel {
  background: #171e29;
  border-color: #2a3a4e;
}

.icon-search-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1.5px solid #e7e7e7;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 0.75rem;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.dark-theme .icon-search-wrap {
  background: #212e3e;
  border-color: #2a3a4e;
}
.icon-search-wrap:focus-within {
  border-color: #7f77dd;
  box-shadow: 0 0 0 3px rgba(127, 119, 221, 0.1);
}
.icon-search-magnifier {
  color: #aaa;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.icon-search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-family: 'Poppins', sans-serif;
  font-size: 0.82rem;
  color: #213547;
  outline: none;
}
.dark-theme .icon-search-input {
  color: #d4d2d2;
}
.icon-search-input::placeholder {
  color: #bbb;
}
.icon-search-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}
.icon-search-clear:hover {
  color: #555;
}

.icon-grid {
  max-height: 28vh;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(38px, 1fr));
  gap: 6px;
}
.icon-grid::-webkit-scrollbar {
  width: 4px;
}
.icon-grid::-webkit-scrollbar-track {
  background: transparent;
}
.icon-grid::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 2px;
}
.dark-theme .icon-grid::-webkit-scrollbar-thumb {
  background: #2a3a4e;
}

.icon-item {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  cursor: pointer;
  color: #555;
  font-size: 14px;
  transition:
    background 0.15s,
    color 0.15s,
    transform 0.1s;
}
.dark-theme .icon-item {
  color: #aaa;
}
.icon-item:hover {
  background: var(--purple-50);
  color: var(--color-accent-purple);
  transform: scale(1.1);
}
.dark-theme .icon-item:hover {
  background: #1e1a40;
}
.icon-item.selected {
  background: var(--gradient-primary);
  color: white;
}

.icon-empty {
  text-align: center;
  padding: 1.5rem 0;
}
.icon-empty p {
  font-size: 0.78rem;
  color: #aaa;
  margin: 0;
}

.icon-panel-slide-enter-active,
.icon-panel-slide-leave-active {
  transition: all 0.2s cubic-bezier(0.34, 1.2, 0.64, 1);
}
.icon-panel-slide-enter-from,
.icon-panel-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>

<!-- <template>
  <div class="icon-selector dropdown-container w-100">
    <button
      :class="['dropdown-button w-100', color, styleClass]"
      @click.prevent="toggleDropdown"
    >
      <span v-if="selectedIcon"
        ><font-awesome-icon :icon="selectedIcon"
      /></span>
      <span v-else class="icon-placeholder-text">{{ buttonText }}</span>
      <span class="arrow" :class="{ active: state.iconSearchOpen }"
        ><font-awesome-icon :icon="['fas', 'caret-down']"
      /></span>
    </button>
    <div v-if="state.iconSearchOpen" class="icons-container-wrapper">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="Search for an icon..."
        class="icon-search"
      />
      <div class="icon-grid">
        <div
          v-for="(icon, index) in filteredIcons"
          :key="index"
          @click="selectIcon(icon)"
          class="icon-item"
        >
          <font-awesome-icon :icon="icon" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, reactive, watch } from 'vue';
import { iconsArray } from '../utils/iconLibrary';

export default {
  name: 'IconSelector',
  props: {
    color: {
      type: String,
      default: 'grey-outline-dropdown',
    },
    buttonText: {
      type: String,
      required: true,
    },
    styleClass: {
      type: String,
      default: '',
    },
    options: {
      type: String,
      default: () => '',
    },
  },
  setup(props, context) {
    const allIcons = ref(iconsArray);
    const searchQuery = ref('');
    const loading = ref(false);
    const selectedIcon = ref(null);
    const state = reactive({
      iconSearchOpen: false,
    });

    const toggleDropdown = () => {
      state.iconSearchOpen = !state.iconSearchOpen;
    };

    const filteredIcons = computed(() => {
      return allIcons.value.filter((icon) => {
        return icon.iconName
          .toLowerCase()
          .includes(searchQuery.value.toLowerCase());
      });
    });

    const selectIcon = (icon) => {
      state.iconSearchOpen = false;
      selectedIcon.value = icon;
      context.emit('optionSelected', `${icon.prefix} ${icon.iconName}`);
    };

    const mapIconFromString = (iconString) => {
      if (!iconString) {
        return null;
      }
      const [prefix, iconName] = iconString.split(' ');
      const icon = allIcons.value.find(
        (icon) => icon.prefix === prefix && icon.iconName === iconName
      );

      return icon || null;
    };

    watch(
      () => props.options,
      (newValue) => {
        if (newValue && props.options.length > 0) {
          selectedIcon.value = mapIconFromString(newValue);
        }
      },
      { immediate: true }
    );

    return {
      allIcons,
      loading,
      searchQuery,
      filteredIcons,
      selectIcon,
      props,
      context,
      state,
      toggleDropdown,
      selectedIcon,
    };
  },
};
</script>

<style scoped>
.icon-selector {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 600px;
  margin: 0 auto;
}

.light-theme .icon-placeholder-text {
  color: #000000;
}

.dark-theme .icon-placeholder-text {
  color: #ddd;
}

.icon-search {
  width: 100%;
  box-sizing: border-box;
  border-radius: 10px;
  padding: 10px;
  font-size: 1em;
  margin-bottom: 20px;
  padding: 10px;
}

.light-theme .icon-search {
  border: 1px solid #ddd;
}

.dark-theme .icon-search {
  background: black;
  border: 1px solid black;
  color: #ffffff;
}

.icon-grid {
  max-height: 30vh;
  overflow-y: scroll;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
  gap: 10px;
  width: 100%;
}

.icon-item {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  border-radius: 5px;
}

.icon-item:hover {
  background-color: #f0f0f0;
}

.loading-spinner {
  font-size: 16px;
  color: #888;
  padding: 20px;
}

.icons-container-wrapper {
  list-style: none;
  margin: 0;
  border-radius: 5px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  z-index: 1;
  width: 100%;
}

.light-theme .icons-container-wrapper {
  background: #f8f8f8;
}

.dark-theme .icons-container-wrapper {
  background: #000000;
}

.dark-theme .dropdown-button {
  color: white;
}

.dropdown-button:focus {
  outline: none;
}

.icons-container {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  text-align: center;
  gap: 1em;
  padding: 1em;
}

.light-theme .icons-container {
  background: #f8f8f8;
}

.dark-theme .icons-container {
  background: black;
}

.arrow {
  margin-left: 0.5em;
  transition: transform 0.2s;
}

.arrow.active {
  transform: rotate(-180deg);
}
</style> -->
